import { theme } from '../theme'
import { Ic } from '../icons'
import { SlotCard } from './SlotCard'
import type { Plan, Recipe, SlotKey } from '../types'
import { DOW_LONG, SLOT_KEYS, addDays, isoDate, monthGridStart } from '../dates'

export function WeekView({
  weekStart, today, plan, recipesById, onSlotClick, onSlotClear,
}: {
  weekStart: Date
  today: Date
  plan: Plan
  recipesById: Map<number, Recipe>
  onSlotClick: (date: string, slot: SlotKey) => void
  onSlotClear?: (date: string, slot: SlotKey) => void
}) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const todayIso = isoDate(today)
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 14, paddingTop: 8,
    }}>
      {days.map((d, i) => {
        const iso = isoDate(d)
        const isToday = iso === todayIso
        const dp = plan[iso]
        const weekend = i >= 5
        return (
          <div key={iso} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', gap: 8,
              paddingBottom: 8, borderBottom: `1px solid ${theme.line}`,
            }}>
              <div style={{
                fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.2, fontWeight: 600,
                textTransform: 'uppercase',
                color: isToday ? theme.accent : (weekend ? theme.muted : theme.inkSoft),
              }}>{DOW_LONG[i]}</div>
              <div style={{
                fontFamily: theme.serif, fontSize: 26, fontWeight: theme.headWeight,
                color: isToday ? theme.accent : theme.ink, lineHeight: 1,
              }}>{d.getDate()}</div>
              {isToday && <div style={{
                marginLeft: 'auto',
                fontFamily: theme.sans, fontSize: 9, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                color: theme.accent,
              }}>Today</div>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {SLOT_KEYS.map(sk => {
                const m = dp?.[sk]
                const r = m?.recipe_id ? recipesById.get(m.recipe_id) : undefined
                return (
                  <SlotCard key={sk} slotKey={sk} meal={m} recipe={r}
                    onClick={() => onSlotClick(iso, sk)}
                    onClear={m && r && onSlotClear ? () => onSlotClear(iso, sk) : undefined} />
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function MonthView({
  monthDate, today, plan, recipesById, onSlotClick,
}: {
  monthDate: Date
  today: Date
  plan: Plan
  recipesById: Map<number, Recipe>
  onSlotClick: (date: string, slot: SlotKey) => void
}) {
  const gridStart = monthGridStart(monthDate)
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))
  const month = monthDate.getMonth()
  const todayIso = isoDate(today)

  return (
    <div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0,
        paddingBottom: 8, borderBottom: `1px solid ${theme.line}`, marginBottom: 8,
      }}>
        {DOW_LONG.map(d => (
          <div key={d} style={{
            fontFamily: theme.sans, fontSize: 10, letterSpacing: 1.2, fontWeight: 600,
            textTransform: 'uppercase', color: theme.inkSoft, padding: '0 8px',
          }}>{d}</div>
        ))}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gridAutoRows: 'minmax(112px, 1fr)', gap: 6,
      }}>
        {days.map(d => {
          const iso = isoDate(d)
          const inMonth = d.getMonth() === month
          const isToday = iso === todayIso
          const dp = plan[iso]
          return (
            <div key={iso} style={{
              background: inMonth ? theme.surface : 'transparent',
              border: theme.cardBorder, borderRadius: theme.radius,
              padding: '8px 8px',
              opacity: inMonth ? 1 : 0.45,
              display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{
                  fontFamily: theme.serif, fontSize: 18, fontWeight: theme.headWeight,
                  color: isToday ? theme.accent : theme.ink, lineHeight: 1,
                }}>{d.getDate()}</div>
                {isToday && <div style={{
                  fontFamily: theme.sans, fontSize: 8, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                  color: '#fff', background: theme.accent, padding: '2px 5px', borderRadius: 999,
                }}>Today</div>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                {SLOT_KEYS.map(sk => {
                  const m = dp?.[sk]
                  const r = m?.recipe_id ? recipesById.get(m.recipe_id) : undefined
                  if (!r) {
                    return (
                      <button key={sk} onClick={() => onSlotClick(iso, sk)} style={{
                        all: 'unset', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '2px 4px', borderRadius: 3,
                        fontFamily: theme.sans, fontSize: 10, color: theme.muted,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, border: `1px dashed ${theme.lineStrong}` }}/>
                        <span>{sk === 'breakfast' ? 'Brk' : sk === 'lunch' ? 'Lun' : 'Din'}</span>
                      </button>
                    )
                  }
                  return (
                    <button key={sk} onClick={() => onSlotClick(iso, sk)} style={{
                      all: 'unset', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '2px 4px', borderRadius: 3, minWidth: 0,
                      opacity: m?.status === 'skipped' ? 0.5 : 1,
                    }}>
                      <span style={{ flex: '0 0 auto', width: 8, height: 8, borderRadius: 2,
                        background: `oklch(0.78 0.10 ${r.hue})` }}/>
                      <span style={{
                        fontFamily: theme.sans, fontSize: 11, color: theme.ink, fontWeight: 500,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        textDecoration: m?.status === 'skipped' ? 'line-through' : 'none',
                      }}>{r.name}</span>
                      {m?.status === 'cooked' && (
                        <Ic.check width="9" height="9" style={{ color: theme.cooked, flex: '0 0 auto' }}/>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
